import { useAtomValue } from "jotai";
import { SetOptional } from "type-fest";
import { fullContext } from "./local";
import { StatefulFilters } from "./filters";

const DB_NAME = "filterStatesDB";
const STORE_NAME = "filterStates";
/**
 * Increment version to force upgrade any time you change the object store schema.
 */
const DB_VERSION = 8;
let db: IDBDatabase | undefined;

async function initializeDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Check if IndexedDB is available (not available in some test environments)
    if (!globalThis.indexedDB) {
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);

    request.addEventListener("error", () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(request.error);
    });

    request.addEventListener("upgradeneeded", (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      // Delete existing object store if it exists (to handle schema changes)
      if (database.objectStoreNames.contains(STORE_NAME)) {
        database.deleteObjectStore(STORE_NAME);
      }

      // Create the object store with the correct schema, upgrade version above if you change this!
      const objectStore = database.createObjectStore(STORE_NAME, {
        keyPath: "keyString", // Use single string key for exact matches
      });

      // Add indexes for partial matching
      objectStore.createIndex("namespace", "namespace", { unique: false });
      objectStore.createIndex("role", "role", { unique: false });
      objectStore.createIndex("rootContextId", "rootContextId", {
        unique: false,
      });
      objectStore.createIndex("filteredContextId", "filteredContextId", {
        unique: false,
      });
      objectStore.createIndex(
        "contextFiltered",
        ["rootContextId", "filteredContextId"],
        { unique: false },
      );
      objectStore.createIndex(
        "contextApp",
        ["filteredContextId", "namespace"],
        {
          unique: false,
        },
      );
      objectStore.createIndex(
        "contextMulti1",
        ["filteredContextId", "isMultiOp", "isMultiYear"],
        { unique: false },
      );
      objectStore.createIndex(
        "contextMulti2",
        ["filteredContextId", "isMultiOp"],
        { unique: false },
      );
      objectStore.createIndex(
        "contextMulti3",
        ["filteredContextId", "isMultiYear"],
        { unique: false },
      );
    });

    request.addEventListener("success", () => {
      resolve(request.result);
    });
  });
}

/**
 * Helper functions for boolean handling in IndexedDB
 */
function booleanToString(value: boolean | undefined): string {
  if (value === undefined) return "undefined";
  return value ? "true" : "false";
}

/**
 * Convert string representation of boolean back to actual boolean type.
 */
function stringToBoolean(value: string): boolean | undefined {
  if (value === "undefined") return undefined;
  return value === "true";
}

/**
 * Convert FilterKey with booleans to storage format (all strings)
 */
function normalizeKeyForStorage(
  key: Partial<FilterKey>,
): Record<string, string> {
  return {
    rootContextId: key.rootContextId ?? "",
    role: key.role ?? "",
    filteredContextId: key.filteredContextId ?? "",
    namespace: key.namespace ?? "*",
    isMultiOp: booleanToString(key.isMultiOp),
    isMultiYear: booleanToString(key.isMultiYear),
  };
}

/**
 * Convert storage format back to FilterKey with proper boolean types
 */
export function denormalizeKeyFromStorage(
  stored: Record<string, string | undefined>,
): FilterKey {
  return {
    rootContextId: stored.rootContextId ?? "",
    role: stored.role ?? "",
    filteredContextId: stored.filteredContextId ?? "",
    namespace: "*",
    isMultiOp: stringToBoolean(stored.isMultiOp ?? "undefined"),
    isMultiYear: stringToBoolean(stored.isMultiYear ?? "undefined"),
  };
}

/**
 * Generate keyString with boolean normalization - only for complete keys
 */
export function generateKeyString(key: FilterKey): string {
  const normalized = normalizeKeyForStorage(key);
  return JSON.stringify([
    normalized.rootContextId,
    normalized.role,
    normalized.filteredContextId,
    normalized.namespace,
    normalized.isMultiOp,
    normalized.isMultiYear,
  ]);
}

/**
 * Generate keyString for partial keys (used in fallback scenarios)
 */
export function generatePartialKeyString(
  key: Partial<FilterKey>,
): string | undefined {
  // Only generate if we have required fields
  if (!key.rootContextId || !key.role || !key.filteredContextId) {
    return undefined;
  }

  const normalized = normalizeKeyForStorage(key);
  return JSON.stringify([
    normalized.rootContextId,
    normalized.role,
    normalized.filteredContextId,
    normalized.namespace,
    normalized.isMultiOp,
    normalized.isMultiYear,
  ]);
}

export function generateKeyStringOld(keyObj: Partial<FilterKey>): string {
  return JSON.stringify([
    keyObj.rootContextId,
    keyObj.filteredContextId,
    keyObj.role,
    keyObj.namespace,
    !!keyObj.isMultiOp,
    !!keyObj.isMultiYear,
    // keyObj.isMultiCrop
  ]);
}

export async function getDbStore() {
  try {
    db ??= await initializeDB();

    const transaction = db.transaction([STORE_NAME], "readwrite");
    return transaction.objectStore(STORE_NAME);
  } catch {
    return;
  }
}

export async function getReadOnlyStore() {
  try {
    db ??= await initializeDB();

    const transaction = db.transaction([STORE_NAME], "readonly");
    return transaction.objectStore(STORE_NAME);
  } catch {
    return;
  }
}

export async function performStoreOperation<T>(
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const store = await getDbStore();
  if (!store) {
    // Return a default value if IndexedDB is not available
    return undefined as T;
  }

  return new Promise((resolve, reject) => {
    try {
      const request = operation(store);

      request.addEventListener("success", () => {
        resolve(request.result);
      });

      request.addEventListener("error", () => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(request.error);
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(error);
    }
  });
}

export async function performReadOperation<T>(
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const store = await getReadOnlyStore();
  if (!store) {
    // Return undefined if IndexedDB is not available
    return undefined as T;
  }

  return new Promise((resolve, reject) => {
    try {
      const request = operation(store);

      request.addEventListener("success", () => {
        // Denormalize the result to convert strings back to booleans
        const result = request.result;
        if (Array.isArray(result)) {
          // For arrays, denormalize each item
          const denormalizedArray = result
            .map((item) =>
              denormalizeDataFromStorage(item as unknown as StoredFilterData),
            )
            .filter((item): item is StoredFilterData => item !== undefined);
          resolve(denormalizedArray as unknown as T);
        } else if (result) {
          // For single items, denormalize and resolve
          const denormalized = denormalizeDataFromStorage(
            result as unknown as StoredFilterData,
          );
          resolve(denormalized as unknown as T);
        } else {
          // Handle null/undefined results
          resolve(result);
        }
      });

      request.addEventListener("error", () => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(request.error);
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      reject(error);
    }
  });
}

// Tracks in-flight IndexedDB writes per keyString so callers can await
// completion before reading (prevents write-read race on context switch).
const pendingWrites = new Map<string, Promise<IDBValidKey | undefined>>();

/**
 * Returns a promise that resolves when the in-flight write for the given
 * keyString completes. Resolves immediately if no write is pending.
 */
export function awaitPendingWrite(
  keyString: string | undefined,
): Promise<void> {
  if (!keyString) return Promise.resolve();
  const pending = pendingWrites.get(keyString);
  if (!pending) return Promise.resolve();
  return pending.then(noop, noop);
}

function noop() {}

/**
 * Helper function to store data with proper normalization
 */
export async function storeNormalizedData(
  data: StoredFilterData,
): Promise<IDBValidKey | undefined> {
  try {
    const normalizedData = normalizeDataForStorage(data);
    const keyString = normalizedData.keyString;
    const writePromise = performStoreOperation((store) =>
      store.put(normalizedData),
    );
    pendingWrites.set(keyString, writePromise);
    void writePromise.finally(() => {
      if (pendingWrites.get(keyString) === writePromise) {
        pendingWrites.delete(keyString);
      }
    });
    return await writePromise;
  } catch {
    // Silently fail if IndexedDB is not available (e.g., in tests)
    return undefined;
  }
}

/**
 * Clears data for a given filter key.
 */
export async function clearFilterKeyData(key: FilterKey): Promise<void> {
  try {
    return performStoreOperation((store) =>
      store.delete(generateKeyString(key)),
    );
  } catch {
    // Silently fail if IndexedDB is not available (e.g., in tests)
    return;
  }
}

/**
 * This is where most of the restoration of filter state business logic will happen.
 * TODO: 😭 thorough testing to match expected business logic here
 * @param primaryOperation - The primary database operation to attempt first
 * @param fallbackQueries - Array of fallback queries to try if primary fails
 * @returns The result of the operation or undefined if all attempts fail
 */
export async function performReadOperationWithFallback<
  T extends StoredFilterData,
>(
  primaryOperation: (store: IDBObjectStore) => IDBRequest<T>,
  fallbackQueries: Array<{
    indexName: string;
    value: IDBValidKey | IDBKeyRange;
    description: string;
  }>,
): Promise<T | undefined> {
  try {
    // Try primary operation first
    const primaryResult = await performReadOperation(primaryOperation);
    if (primaryResult) {
      return primaryResult;
    } else {
      console.log("Primary operation returned no results, trying fallbacks");
    }
  } catch (error) {
    console.log("Primary operation failed, trying fallbacks.", error);
  }

  // Try fallback queries in order
  for (const fallback of fallbackQueries) {
    try {
      const result = await performReadOperation<T>((store) => {
        const index = store.index(fallback.indexName);
        return index.get(fallback.value) as IDBRequest<T>;
      });

      if (result) {
        return result;
      }
    } catch (error) {
      console.log(`Fallback ${fallback.description} failed:`, error);
    }
  }

  return undefined;
}

export type FilterKey = {
  rootContextId: string;
  role: string;
  filteredContextId: string;
  namespace: "*"; // all logic currently shares the same appName so lookups are shared
  isMultiOp?: boolean;
  isMultiYear?: boolean;
};

export function useFilterKey(
  partialKey: SetOptional<
    FilterKey,
    "rootContextId" | "filteredContextId" | "role"
  >,
): FilterKey {
  // look up any missing values from the global application state
  // to merge with the provided key-components.
  // The first three can be assumed from global state,
  // but the rest of keys are required from each app.
  const ctx = useAtomValue(fullContext);
  if (!ctx) {
    throw new Error(
      "root context was undefined, can't create a filter key",
      ctx,
    );
  }
  const {
    rootBusinessId: rootContextId,
    rootRole: role,
    filteredBusinessId: filteredContextId,
  } = ctx;
  return Object.assign(
    { rootContextId, role, filteredContextId, namespace: "*" } as FilterKey,
    partialKey,
  );
}

/**
 * Helper function to validate if stored data is compatible with the requesting app
 */
function isDataCompatibleWithApp(
  data: StatefulFilters | undefined,
  requestingKey: Partial<FilterKey>,
): boolean {
  if (!data) return false;

  // If the requesting app is single-op (isMultiOp: false) but the stored data
  // has multiple operations, it's not compatible
  if (
    requestingKey.isMultiOp === false &&
    data.operations &&
    data.operations.length > 1
  ) {
    return false;
  }

  return true;
}

export async function getFiltersWithFallback(
  keyObj: Partial<FilterKey>,
): Promise<StatefulFilters | undefined> {
  try {
    // Build fallback queries array based on available key members
    const fallbackQueries: Array<{
      indexName: string;
      value: IDBValidKey | IDBKeyRange;
      description: string;
    }> = [];

    // Only attempt exact key match if all required fields are present
    let primaryOperation:
      | ((store: IDBObjectStore) => IDBRequest<StoredFilterData>)
      | undefined = undefined;

    if (
      keyObj.rootContextId &&
      keyObj.role &&
      keyObj.filteredContextId &&
      keyObj.namespace
    ) {
      const keyString = generatePartialKeyString(keyObj);
      if (keyString) {
        primaryOperation = (store) => store.get(keyString);
      }
    }

    // Add compound index fallbacks if both required fields exist
    // Order of fallbacks matters, so we try the most specific first
    // TODO refactor this to use a pattern-matching library like `ts-pattern` or `ts-effect/Micro`
    if (keyObj.filteredContextId && keyObj.isMultiOp === true) {
      fallbackQueries.push({
        indexName: "contextMulti2",
        value: [keyObj.filteredContextId, "true"],
        description: "context and single (multi-op true) match",
      });
    } else {
      if (
        keyObj.filteredContextId &&
        keyObj.isMultiOp !== undefined &&
        keyObj.isMultiYear !== undefined
      ) {
        fallbackQueries.push({
          indexName: "contextMulti1",
          value: [
            keyObj.filteredContextId,
            booleanToString(keyObj.isMultiOp),
            booleanToString(keyObj.isMultiYear),
          ],
          description: "context and multi-op/year match",
        });
      }

      if (
        keyObj.filteredContextId &&
        keyObj.isMultiOp !== undefined &&
        keyObj.isMultiYear === undefined
      ) {
        fallbackQueries.push({
          indexName: "contextMulti2",
          value: [keyObj.filteredContextId, booleanToString(keyObj.isMultiOp)],
          description: "context and multi-op match",
        });
      }

      if (
        keyObj.filteredContextId &&
        keyObj.isMultiOp === undefined &&
        keyObj.isMultiYear !== undefined
      ) {
        fallbackQueries.push({
          indexName: "contextMulti3",
          value: [
            keyObj.filteredContextId,
            booleanToString(keyObj.isMultiYear),
          ],
          description: "context and multi-year match",
        });
      }

      if (keyObj.filteredContextId) {
        fallbackQueries.push({
          indexName: "filteredContextId",
          value: keyObj.filteredContextId,
          description: "filtered context match",
        });
      }
      // Note: We do not want a fallback query finding *only* root context id
    }

    // If no primary operation possible, try fallbacks directly
    if (fallbackQueries.length === 0) {
      console.warn("No valid key members provided for filter lookup");
      return undefined;
    }

    // If we have a primary operation, try it first
    if (primaryOperation) {
      try {
        const primaryResult = await performReadOperation(primaryOperation);
        if (primaryResult && isDataCompatibleWithApp(primaryResult, keyObj)) {
          return primaryResult;
        }
      } catch {
        // Continue to fallbacks
      }
    }

    // Try each fallback query in order
    for (const fallback of fallbackQueries) {
      try {
        const result = await performReadOperation<StatefulFilters | undefined>(
          (store) => {
            const index = store.index(fallback.indexName);
            return index.get(fallback.value) as IDBRequest<
              StatefulFilters | undefined
            >;
          },
        );

        if (result && isDataCompatibleWithApp(result, keyObj)) {
          return result;
        }
      } catch {
        // Continue to next fallback
      }
    }

    return undefined;
  } catch {
    // If IndexedDB is not available (e.g., in tests), return undefined
    return undefined;
  }
}

/**
 * Extended type for storage that includes all possible fields
 */
type StoredFilterData = StatefulFilters & {
  keyString: string;
  rootContextId?: string;
  role?: string;
  namespace?: string;
  isMultiOp?: boolean | string;
  isMultiYear?: boolean | string;
};

/**
 * Normalize data for storage (convert booleans to strings)
 */
export function normalizeDataForStorage(
  data: StoredFilterData,
): StoredFilterData {
  const normalized = { ...data };

  // Convert boolean fields to strings for consistent IndexedDB storage
  if (typeof normalized.isMultiOp === "boolean") {
    normalized.isMultiOp = booleanToString(normalized.isMultiOp);
  }
  if (typeof normalized.isMultiYear === "boolean") {
    normalized.isMultiYear = booleanToString(normalized.isMultiYear);
  }

  return normalized;
}

/**
 * Denormalize data from storage (convert strings back to booleans)
 */
export function denormalizeDataFromStorage(
  data: StoredFilterData | undefined,
): StoredFilterData | undefined {
  if (!data) return data;

  const denormalized = { ...data };

  // Convert string fields back to booleans
  if (typeof denormalized.isMultiOp === "string") {
    denormalized.isMultiOp = stringToBoolean(denormalized.isMultiOp);
  }
  if (typeof denormalized.isMultiYear === "string") {
    denormalized.isMultiYear = stringToBoolean(denormalized.isMultiYear);
  }

  return denormalized;
}
